<template>
  <v-dialog
    v-model="isOpen"
    max-width="1000"
    scrollable
    content-class="updateDraftDialog"
    hide-overlay
    fullscreen
    transition="dialog-bottom-transition"
  >
    <loading-plan-dialog ref="loadingPlanDialog" message="Please wait while we update your plan" />
    <select-debt-dialog 
      ref="selectDebtDialog" 
      :household="household" 
      :ignore-debts="debtIdsForIgnore"
      @on-select="new_debts.push($event)"
      @on-debt-updated="$emit('on-debt-updated',$event)"
    />
    <select-policy-dialog 
      ref="selectPolicyDialog"
      :household="household"
      :ignore-policies="policyIdsForIgnore"
      @on-select="new_policies.push($event)"
      @on-policy-updated="$emit('on-policy-updated',$event)"
    />

    <v-dialog
      v-model="advancedSettingsDialog"
      max-width="400px"
    >
      <v-card>
        <v-card-title>Advanced Plan Settings</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" class="pr-1">
              <div class="overline mb-1">Additional Loan Principal</div>
              <v-text-field
                v-model="new_additional_loan_principal"
                outlined
                dense
                hide-details
                type="number"
                min="0"
                step="1"
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="secondary" @click="advancedSettingsDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-card>
      <v-card-title class="pa-0">
        <v-toolbar
          dark
          color="primary"
        >
          <v-toolbar-title>Update Your WealthyIQ Plan</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn
              icon
              dark
              @click="close()"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
      </v-card-title>
      <v-card-text>
        <v-container fluid>
          <v-row class="justify-center">
            <v-col md="3">
              <div class="caption">Plan Start Date</div>
              <v-menu
                ref="newStartMenu"
                v-model="newStartMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="new_start_date_formatted"
                    dense
                    outlined
                    placeholder="Choose..."
                    v-bind="attrs"
                    v-on="on"
                    @blur="new_start_date = parseDate(new_start_date_formatted)"
                  />
                </template>
                <v-date-picker v-model="new_start_date" no-title scrollable @input="newStartMenu = false" type="month" />
              </v-menu>
            </v-col>

            <v-col md="3">
              <div class="caption">Additional Capital</div>     
              <v-text-field
                v-model="new_additional_capital"
                dense
                outlined
                placeholder="$0"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" class="display-1 text-center">Liabilities</v-col>
          </v-row>
          <v-row class="justify-center">
            <v-col
              v-for="debt in debtsSorted"
              :key="'debt' + debt.id"
              cols="12"
              sm="6"
              md="4"
              lg="3"
            >
              <debt-card 
                :debt="debt"
                delete-text="Remove"
                no-click
                @on-delete="removeDebt(debt)"
              />
            </v-col>
          </v-row>
          <v-row class="justify-center">
            <v-col cols="3">
              <debt-card 
                :debt="{}"
                @on-click="$refs.selectDebtDialog.open()"
                dense
              />
            </v-col>
          </v-row>

          <v-divider class="mt-8 mb-4" />

          <v-row>
            <v-col cols="12" class="display-1 text-center">Policies</v-col>
          </v-row>
          <v-row class="justify-center">
            <v-col
              v-for="policy in policiesSorted"
              :key="'policy' + policy.id"
              cols="12"
              sm="6"
              md="4"
              lg="3"
            >
              <policy-card 
                :policy="policy"
                delete-text="Remove"
                @on-delete="removePolicy(policy)"
                no-click
              />
            </v-col>
          </v-row>
          <v-row class="justify-center">
            <v-col cols="3">
              <policy-card 
                :policy="{}"
                @on-click="$refs.selectPolicyDialog.open()"
                dense
              />
            </v-col>
          </v-row>

        </v-container>
      </v-card-text>
      <v-card-actions class="justify-center pb-8" style="position: relative;">
        <v-btn large text color="secondary" @click="close()">Close</v-btn>
        <v-btn large color="primary" dark @click="save()">Save</v-btn>

        <v-btn v-if="showAdvancedButton" @click="advancedSettingsDialog = true" text color="grey" small style="position: absolute; right: 10px; bottom: 10px;">Advanced</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import LoadingPlanDialog from './LoadingPlanDialog'
import DebtCard from '@/views/households/liabilities/Card'
import SelectDebtDialog from './SelectDebtDialog'
import PolicyCard from '@/views/households/policies/Card'
import SelectPolicyDialog from './SelectPolicyDialog'

export default {
  name: 'UpdateDraftDialog',
  props: {
    household: {
      type: Object,
      required: true
    },
    plan: {
      type: Object,
      required: true
    }
  },
  components: {
    LoadingPlanDialog,
    DebtCard,
    SelectDebtDialog,
    PolicyCard,
    SelectPolicyDialog
  },
  data() {
    return {
      isOpen: false,
      advancedSettingsDialog: false,
      showAdvancedButton: false,

      newStartMenu: false,
      new_start_date: null,
      new_start_date_formatted: null,

      new_additional_capital: null,
      new_debts: [],
      new_policies: [],

      new_additional_loan_principal: null
    }
  },
  mounted() {
    document.addEventListener('keydown', this.keyCheck)
    document.addEventListener('keyup', this.keyCheck)
  },
  beforeDestroy() {
    document.removeEventListener('keydown',this.keyCheck)
    document.removeEventListener('keyup',this.keyCheck)
  },
  watch: {
    new_start_date ( newVal ) {
      this.new_start_date_formatted = this.formatDate( newVal )
    },
    isOpen(newVal) {
      if(newVal) this.reset()
    }
  },
  computed: {
    debtsSorted() {
      let debts = JSON.parse(JSON.stringify(this.new_debts))
      debts.sort((a,b) => parseInt(b.balance) - parseInt(a.balance))
      return debts
    },
    debtIdsForIgnore() {
      let arr = []
      this.debtsSorted.map(d => {
        arr.push(d.id)
        if(d.original_debt_id) arr.push(d.original_debt_id)
      })
      return arr
    },
    finalDebtIds() {
      return this.debtsSorted.map(d => d.id)
    },
    policiesSorted() {
      return JSON.parse(JSON.stringify(this.new_policies))
    },
    policyIdsForIgnore() {
      let arr = []
      this.policiesSorted.map(p => {
        arr.push(p.id)
        if(p.original_policy_id) arr.push(p.original_policy_id)
      })
      return arr
    },
    finalPolicyIds() {
      return this.policiesSorted.map(p => p.id)
    },
  },
  methods: {
    open() {
      this.isOpen = true
    },
    close() {
      this.isOpen = false
    },
    reset() {
      this.new_start_date           = this.$moment(this.plan.start_date).format('YYYY-MM')
      this.new_additional_capital   = this.plan.additional_capital || null
      let debtArr                   = []
      for(let i = 0; i < Object.keys(this.plan.data.debts).length; i++) {
        let key = Object.keys(this.plan.data.debts)[i]
        debtArr.push( this.plan.data.debts[key] )
      }
      this.new_debts                = debtArr

      let policyArr                 = []
      for(let i = 0; i < Object.keys(this.plan.data.policies).length; i++) {
        let key = Object.keys(this.plan.data.policies)[i]
        policyArr.push( this.plan.data.policies[key] )
      }
      this.new_policies             = policyArr
      this.new_additional_loan_principal = this.plan.additional_policy_replenishment
      this.advancedSettingsDialog = false
    },
    formatDate (date) {
      if (!date) return null

      const [year, month] = date.split('-')
      return `${month}/${year}`
    },
    parseDate (date) {
      if (!date) return null

      let [month, year, year2] = date.split('/')
      if(year2) year = year2
      return `${year}-${month.padStart(2, '0')}`
    },
    save() {
      this.$refs.loadingPlanDialog.open()

      let data = {
        debtsToUse: this.finalDebtIds,
        policiesToUse: this.finalPolicyIds,
        start_date: this.new_start_date + '-05',
        additional_capital: this.new_additional_capital || null,
        additional_policy_replenishment: this.new_additional_loan_principal || null
      }
      this.$http.put('household-plans/' + this.plan.id, data)
        .then(response => {
          this.$emit('on-plan-updated', response.data)
          this.close()
        })
        .catch(() => {
          this.$swal.fire('Error', 'There was an error updating the plan. Please try again.', 'error')
        })
        .finally(() => {
          this.$refs.loadingPlanDialog.close()
        })
    },
    removeDebt(debt) {
      this.new_debts = this.new_debts.filter(d => d.id != debt.id)
    },
    removePolicy(policy) {
      this.new_policies = this.new_policies.filter(p => p.id != policy.id)
    },

    keyCheck (e) {
      if( (e.shiftKey && e.metaKey) || (e.shiftKey && e.ctrlKey) ) {
        this.showAdvancedButton = true
      } else {
        this.showAdvancedButton = false
      }
    },
  }
}
</script>

<style lang="scss">
</style>